import { useState } from 'react'
import { Alert, Button, Snackbar } from '@mui/material'
import { useT } from '@/i18n/useT'

interface TrashUndoSnackbarProps {
  /** Id of the invoice just moved to the trash; null hides the snackbar. */
  invoiceId: string | null
  /** True when the trashed invoice was still under review in Flixpector. */
  underReview?: boolean
  onUndo: (id: string) => Promise<void>
  onClose: () => void
}

export function TrashUndoSnackbar({ invoiceId, underReview, onUndo, onClose }: TrashUndoSnackbarProps) {
  const { t } = useT()
  const [busy, setBusy] = useState(false)

  const handleUndo = async () => {
    if (!invoiceId || busy) return
    setBusy(true)
    try {
      await onUndo(invoiceId)
    } catch (err) {
      console.error('[trash] undo failed', err)
    } finally {
      setBusy(false)
      onClose()
    }
  }

  return (
    <Snackbar
      key={invoiceId ?? 'none'}
      open={!!invoiceId}
      autoHideDuration={8000}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return
        onClose()
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={onClose}
        action={
          <Button color="inherit" size="small" disabled={busy} onClick={() => void handleUndo()}>
            {t('Undo')}
          </Button>
        }
      >
        {t('Moved to trash')}
        {underReview ? ` · ${t('Still under review in Flixpector.')}` : ''}
      </Alert>
    </Snackbar>
  )
}
